import React from "react";
import cx from "classnames";
import styles from "./createStoryboardDialog.module.scss";
import InteractiveContentIcon from "./svg/InteractiveContentIcon";
import LandingPageIcon from "./svg/LandingPageIcon";
import ResourceCenterIcon from "./svg/ResourceCenterIcon";
import SpatialIcon from "./svg/SpatialIcon";
import FlipbookIcon from "./svg/Flipbook";
import {
  STORYBOARD_TYPES,
  StoryboardType,
} from "src/types/experiences/storyboardSettings/storyboard";

const DEFAULT_ICON_COLOR = "#A9A9A9";
const primaryColor = "#E51058";

interface ContentTypeOption {
  value: StoryboardType;
  label: string | React.ReactNode;
  Icon: React.FC<{ color?: string; className?: string }>;
}

const CONTENT_TYPES: ContentTypeOption[] = [
  {
    value: STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2,
    label: <>Interactive <br />content</>,
    Icon: InteractiveContentIcon,
  },
  {
    value: STORYBOARD_TYPES.LANDING_PAGE,
    label: "Landing page",
    Icon: LandingPageIcon,
  },
  {
    value: STORYBOARD_TYPES.RESOURCE_CENTER,
    label: "Resource center",
    Icon: ResourceCenterIcon,
  },
  {
    value: STORYBOARD_TYPES.SPATIAL,
    label: "Spatial content",
    Icon: SpatialIcon,
  },
  {
    value: STORYBOARD_TYPES.FLIP_BOOK,
    label: "Flipbook",
    Icon: FlipbookIcon,
  },
];

interface ContentTypeSelectorProps {
  selected: StoryboardType | null;
  onSelect: (value: StoryboardType) => void;
  allowedTypes?: StoryboardType[];
  description?: string;
}

export const ContentTypeSelector: React.FC<ContentTypeSelectorProps> = ({
  selected,
  onSelect,
  allowedTypes,
  description = "Choose the experience that best showcases your content and resonates with your audience.",
}) => {
  const visibleTypes = allowedTypes
    ? CONTENT_TYPES.filter((option) => allowedTypes.includes(option.value))
    : CONTENT_TYPES;

  return (
    <div className={styles.contentTypeSection}>
      <p className={styles.contentTypeDescription}>{description}</p>
      <div className={styles.contentTypeGrid}>
        {visibleTypes.map(({ value, label, Icon }) => {
          const isSelected = selected === value;
          const iconColor = isSelected ? primaryColor : DEFAULT_ICON_COLOR;
          return (
            <div
              key={value}
              className={cx(styles.contentTypeTile, {
                [styles.selected]: isSelected,
              })}
              onClick={() => onSelect(value)}
            >
              <span
                className={cx(styles.tileLabel, {
                  [styles.tileLabelSelected]: isSelected,
                })}
              >
                {label}
              </span>
              <Icon color={iconColor} className={styles.tileIcon} />
            </div>
          );
        })}
      </div>
    </div>
  );
};
